import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty, isEqual, isNil, snakeCase } from 'lodash';

import ActionButton from 'components/buttons/action-button/ActionButton';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import Spinner from 'components/spinner/Spinner';
import Tooltip from 'components/Tooltip';
import CreateNewProfilesButton from 'modules/create-new-profiles/components/create-new-profiles-button/CreateNewProfilesButton';

import ListsContextMenu from '../context-menu/ListsContextMenu';

import { t } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import { abbrNum } from 'utils/helper/common';
import { renderPrice } from 'utils/helper/price';
import { isCustomList, isSmartList } from 'utils/list';
import { getEntityFromProfile } from 'utils/view';
import { getFormattedPercentageRate } from 'utils/formatters';

import './lists-view-panel.scss';

import type { List, ListsStatsView, LocationWindow } from 'types';

type ListViewMetricCommon = {
  value: string | number | undefined,
  tooltipKey?: string
};

type ListViewMetric = {
  [param: string]: ListViewMetricCommon
}

type StateProps = {
  organizationsListIds: Array<number>
};

type OwnProps = {
  list: List,
  channel: string,
  canManageList: boolean,
  isManageableByOrganization: boolean,
  view: ListsStatsView,
  location: LocationWindow,
  isLoading: boolean,
  onDelete: () => void,
  onShowStats: (a: string) => void,
  onShowCreators: () => void
};

type Props = StateProps & OwnProps;

// TODO: replace with StatisticsSidePanel someday
class ListsViewPanel extends React.Component<Props> {
  // noinspection JSCheckFunctionSignatures
  shouldComponentUpdate(nextProps: Props): boolean {
    const { list, view, isLoading } = this.props;

    return !isEqual(list, nextProps.list)
      || view !== nextProps.view
      || isLoading !== nextProps.isLoading;
  }

  isEnabledLightweightProfileAddition(): boolean {
    const { list, organizationsListIds } = this.props;
    if (!organizationsListIds) {
      return false;
    }

    return isSmartList(list) && organizationsListIds.indexOf(list.organization.id) !== -1;
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="lists-view-panel__spinner">
        <Spinner
          position="inline"
          size="tiny"
          color="light"
        />
      </div>
    );
  }

  renderContextMenu(): React.ReactNode | undefined | null {
    const { list, canManageList, isManageableByOrganization, onDelete, view, location, onShowStats, onShowCreators } = this.props;

    if (isNil(list.organization)) {
      return null;
    }

    return (
      <div className="lists-view-panel__context-menu">
        <ListsContextMenu
          key={list.id}
          view={view}
          orgId={list.organization.id}
          list={list}
          location={location}
          isManageableByOrganization={isManageableByOrganization}
          canManageList={canManageList}
          onDelete={onDelete}
          onShowStats={onShowStats}
          onShowCreators={onShowCreators}
        />
      </div>
    );
  }

  renderHeader(): React.ReactNode {
    const { list } = this.props;

    if (isEmpty(list)) {
      return null;
    }

    const brandProfile = list.brandProfile;
    const url = brandProfile?.id ? `/brand/profiles/${brandProfile.id}` : null;
    const entity = getEntityFromProfile(brandProfile);

    return (
      <div className="lists-view-panel__header">
        <Tooltip
          key={brandProfile?.name}
          message={brandProfile?.name}
          width="short"
          display="flex"
        >
          <EntityAvatar
            key={list.id}
            entity={entity}
            link={url}
            customSize={{ width: 48, height: 48 }}
            openInNewTab
            hideTitle
            bordered
          />
        </Tooltip>
        <div className="lists-view-panel__info">
          <span>
            {list.label}
          </span>
          <span>
            {`${t('lists.dashboard.items.label_updated')}: ${getDisplayDate(list.updatedAt)}`}
          </span>
        </div>
        {this.renderContextMenu()}
      </div>
    );
  }

  renderMetrics(): React.ReactNode {
    const { channel, list, isLoading } = this.props;

    if (isEmpty(list)) {
      return null;
    }

    const tooltipKey = 'lists.dashboard.tooltips';

    let metrics:ListViewMetric = {
      creators: {
        value: list.numCreators
      },
      followers: {
        value: abbrNum(list.totalFollowers)
      },
      engagementRate: {
        value: getFormattedPercentageRate(list.averageEngagementRate),
        tooltipKey
      }
    };

    if (channel === 'instagram') {
      metrics = {
        ...metrics,
        cpm: {
          value: renderPrice(list.cpm, { separateThousands: false }),
          tooltipKey
        },
        cpe: {
          value: renderPrice(list.cpe, { separateThousands: false }),
          tooltipKey
        }
      };
    }

    return (
      <div className="lists-view-panel__body">
        {
          Object.keys(metrics).map(key => {
            // $FlowIgnore - Flow doesn't recognize that props may or may not be there
            const metric = metrics[key] || {};
            const skipMetric = channel === 'all' && key === 'engagementRate';

            return !skipMetric && (
              <div
                key={`lists-view-panel-${key}`}
                className="lists-view-panel__metric"
              >
                <div className="lists-view-panel__metric-label">
                  {t(`lists.dashboard.items.label_${snakeCase(key)}`)}
                </div>
                {
                  metric.tooltipKey && (
                    <div className="lists-view-panel__metric-tooltip">
                      <Tooltip
                        key={`lists-view-panel__tooltip-${key}`}
                        message={t(`${tooltipKey}.${snakeCase(key)}`)}
                        showQuestionMark
                      />
                    </div>
                  )
                }
                <div className="lists-view-panel__metric-value">
                  {
                    isLoading
                      ? this.renderSpinner()
                      : metric.value
                  }
                </div>
              </div>
            );
          })
        }
      </div>
    );
  }

  renderDescription(description: string): React.ReactNode {
    return (
      <div className="lists-view-panel__description">
        {description}
      </div>
    );
  }

  renderAddProfileButtons(): React.ReactNode {
    return (
      <div className="lists-view-panel__actions">
        <ActionButton
          newTab
          color="secondary"
          link="/content-creator/profiles"
        >
          {t('lists.dashboard.buttons.add_from_discovery_page')}
        </ActionButton>
      </div>
    );
  }

  render(): React.ReactNode {
    const { list } = this.props;

    return (
      <div className="lists-view-panel">
        {this.renderHeader()}
        {this.renderMetrics()}
        {
          list.description &&
            <React.Fragment>
              <div className="lists-view-panel__divider" />
              {this.renderDescription(list.description)}
            </React.Fragment>
        }
        { isCustomList(list) && this.renderAddProfileButtons() }
        {
          !isEmpty(list) ? (
            <div className="lists-view-panel__create-new-profiles-button">
              <CreateNewProfilesButton
                openedOn={isCustomList(list)
                  ? 'custom-collection'
                  : 'smart-collection'
                }
              />
            </div>
          ) : null
        }
      </div>
    );
  }
}

const mapStateToProps = ({ organizations }): StateProps => ({
  organizationsListIds: organizations.list.map(organization => organization.id)
});

export default connect(mapStateToProps)(ListsViewPanel);
